import { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import { buscarIniciativas } from 'services/iniciativa.service';
import { clearRegistrationUser } from 'utils/fns';
import { IniciativaFormatada } from 'types/iniciativa';
import { registrarAcompanhar } from 'services/iniciativa-action.service';
import { HeaderElbb } from 'pages/HomeElbb/components/Header';
import { IniciativaCardListagem } from './components/IniciativaCardListagem';
import './style.scss';
import { FiCalendar, FiList, FiVideo } from 'react-icons/fi';
import { IoCheckmarkCircle } from 'react-icons/io5';
import DateRangeDisplay from 'components/ui/DateRangeDisplay';
import { useControlDate } from 'hooks/useControlDate';
import { PHASE_IDS } from 'utils/constants';
import { StepIndicator } from 'components/ui/StepIndicator';
import React from 'react';

const elbbSteps = [
  { icon: FiList as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 1, activeColor: 'bg-pink-bb-2', iconColor: 'text-pink-bb-2' },
  { icon: FiVideo as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 2, activeColor: 'bg-pink-bb-2', iconColor: 'text-pink-bb-2' },
  { icon: IoCheckmarkCircle as React.FunctionComponent<React.SVGProps<SVGSVGElement>>, number: 3, activeColor: 'bg-pink-bb-2', iconColor: 'text-pink-bb-2' },
];

export function ListagemInscricaoElbb() {
  const { user } = useAuth();
  const location = useLocation();
  const [iniciativas, setIniciativas] = useState<IniciativaFormatada[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtro, setFiltro] = useState<'disponiveis' | 'inscritas'>('disponiveis');
  const [reloadKey, setReloadKey] = useState(0);
  const [acompanharRegistrado, setAcompanharRegistrado] = useState(false);
  const periodoCadastro = useControlDate(PHASE_IDS.ELBB_INSCRIPTION);

  const disponiveis = iniciativas.filter(ini => ini.status === "aprovada" || ini.status === "inscrita");
  const inscritas = iniciativas.filter(ini => ini.status === "inscrita");

  const counterDisponiveis = `${inscritas.length}/${iniciativas.length} inscritas`;

  const counterInscritas = `${inscritas.length}/${iniciativas.length} inscritas`;

  const listaExibida = filtro === 'disponiveis' ? disponiveis : inscritas;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [location]);

  useEffect(() => {
    if (!user?._id) return;

    async function carregar() {
      try {
        setLoading(true);
        const todas = await buscarIniciativas({ userId: user!._id, categoria: 'ELBB' });
        const userId = clearRegistrationUser(user!._id);

        const minhas = todas.filter((ini) => {
          const ehElbb = ini.categoryPremio === 'ELBB';
          const envolvido =
            clearRegistrationUser(ini.cadastradoMatricula) === userId ||
            clearRegistrationUser(ini.inscritoMatricula) === userId ||
            ini.participantes.some((p) => clearRegistrationUser(p.matricula) === userId);
          return ehElbb && envolvido;
        });

        setIniciativas(minhas);
      } catch {
        setIniciativas([]);
      } finally {
        setLoading(false);
      }
    }

    carregar();
  }, [user, reloadKey]);

  useEffect(() => {
    if (!user?._id || loading || acompanharRegistrado) return;
    const playerId = user._id;
    const possuiIniciativaInscrita = iniciativas.some((ini) => ini.status === 'inscrita');
    if (!possuiIniciativaInscrita) return;
    async function registrarAcompanhamento() {
      try {
        await registrarAcompanhar(playerId);
        setAcompanharRegistrado(true);
      } catch (e) {
        console.warn('Falha ao registrar action_log acompanhar:', e);
      }
    }
    registrarAcompanhamento();
  }, [user?._id, loading, acompanharRegistrado, iniciativas]);

  return (
    <div className="min-h-screen bg-gray-100 ">
      <HeaderElbb currentPage="minhas-iniciativas" />

      {/* Hero banner */}
      <div className="bg-green-bb-2 py-10 pb-12">
        <div className="mx-auto max-w-7xl">
          <h1 className="text-3xl font-bold text-blue-bb m-0 mb-2.5 leading-snug" tabIndex={0}>
            Você possui iniciativa(s) aptas para <br /> inscrição no Prêmio ELBB
          </h1>
          <p className="text-3xl text-white/90 m-0 font-bold" tabIndex={0}>
            Faça a inscrição das iniciativas abaixo.
          </p>
           <div className="flex flex-col sm:flex-row items-start gap-1 text-blue-bb py-6 text-sm sm:text-base">
                      <FiCalendar className="w-4 h-4 sm:w-5 sm:h-5 flex-shrink-0" aria-hidden="true" />
                      <span className="font-semibold">
                        {periodoCadastro ? (
                          <DateRangeDisplay
                            dataInicio={periodoCadastro.dataInicio}
                            dataFim={periodoCadastro.dataFim}
                            dateFormat="dd/MM"
                            prefix="PERÍODO PARA INSCRIÇÃO: "
                            uppercase={false}
                            className="font-semibold "
                          />) : "Período de inscrição não disponível"}
                      </span>
                    </div>
        </div>
      </div>
      {/* Barra de filtro */}
      {!loading && iniciativas.length > 0 && (
        <div className="bg-white border-b border-gray-200">
          <div className="mx-auto max-w-7xl px-4 py-3 flex items-center justify-between gap-4">
            <span className="text-sm font-semibold text-blue-bb" tabIndex={0} aria-live="polite">
              {filtro === 'disponiveis' ? counterDisponiveis : counterInscritas}
            </span>
            <select
              value={filtro}
              onChange={(e) => setFiltro(e.target.value as 'disponiveis' | 'inscritas')}
              aria-label="Filtrar iniciativas"
              className="border border-gray-300 rounded-lg px-3 py-2 text-sm text-gray-700 bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-bb"
            >
              <option value="disponiveis">Iniciativas disponíveis</option>
              <option value="inscritas">Iniciativas já inscritas</option>
            </select>
          </div>
        </div>
      )}

      <main className="px-4 py-10 pb-20">
        <div className="mx-auto max-w-7xl">
          {loading ? (
            <div className="text-center py-16 text-gray-500 text-base" role="status" aria-live="polite">
              <span>Carregando iniciativas...</span>
            </div>
          ) : iniciativas.length === 0 ? (
            <div className="text-center py-16 text-gray-500 text-base" role="status">
              <p tabIndex={0}>Nenhuma iniciativa para inscrição no momento.</p>
            </div>
          ) : listaExibida.length === 0 ? (
            <div className="text-center py-16 text-gray-500 text-base" role="status">
              <p tabIndex={0}>
                {filtro === 'disponiveis'
                  ? 'Nenhuma iniciativa disponível para inscrição.'
                  : 'Nenhuma iniciativa inscrita ainda.'}
              </p>
            </div>
          ) : (
            <div className="flex flex-col gap-8">
              {listaExibida.map((ini) => (
                <IniciativaCardListagem key={ini.id} {...ini} onConfirm={() => setReloadKey(k => k + 1)} />
              ))}
            </div>
          )}
        </div>
      </main>
    </div>
  );
}
